import {
  docFileNameFromStoragePath,
  docFormat,
  docStoragePath,
  fileExtension,
  parseDocLastModified,
} from "./docsManifestDto";

/**
 * Product document rows in `projectstore-metadata`.
 *
 * Canonical documents live at:
 *   pk = "{accountId}:document"
 *   sk = "!document:{documentNameKey}"
 *
 * Product lookup links live at:
 *   pk = "{accountId}:documentByProduct:{productNameKey}"
 *   sk = "!document:{documentNameKey}"
 *
 * The link row is only an index. The UI renders from the canonical document
 * row fetched by `documentPk` / `documentSk`.
 */

export const PRODUCT_DOCUMENT_LINK_ENTITY_TYPE = "documentProductLink";

export interface ProductDocumentLinkDto {
  readonly documentPk?: string;
  readonly documentSk?: string;
  readonly documentName?: string;
  readonly productName?: string;
  readonly archived?: boolean;
  readonly Archived?: boolean;
  readonly isArchived?: boolean;
  readonly IsArchived?: boolean;
  readonly productArchived?: boolean;
  readonly ProductArchived?: boolean;
  readonly isProductArchived?: boolean;
  readonly IsProductArchived?: boolean;
  readonly createdAt?: string;
  readonly updatedAt?: string;
  readonly [extra: string]: unknown;
}

export interface ProductDocumentDto {
  readonly documentName?: string;
  readonly DocumentName?: string;
  readonly name?: string;
  readonly Name?: string;
  readonly fileName?: string;
  readonly FileName?: string;
  readonly storagePath?: string;
  readonly StoragePath?: string;
  readonly fileKey?: string;
  readonly FileKey?: string;
  readonly contentType?: string;
  readonly ContentType?: string;
  readonly fileContentType?: string;
  readonly FileContentType?: string;
  readonly thumbnailUrl?: string;
  readonly ThumbnailUrl?: string;
  readonly thumbnailStoragePath?: string;
  readonly ThumbnailStoragePath?: string;
  readonly userTag?: string;
  readonly UserTag?: string;
  readonly uploadedBy?: string;
  readonly UploadedBy?: string;
  readonly source?: string;
  readonly Source?: string;
  readonly fileSize?: number;
  readonly FileSize?: number;
  readonly size?: number;
  readonly Size?: number;
  readonly sizeBytes?: number;
  readonly SizeBytes?: number;
  readonly fileSizeBytes?: number;
  readonly FileSizeBytes?: number;
  readonly productKeys?: ReadonlyArray<string>;
  readonly ProductKeys?: ReadonlyArray<string>;
  readonly productNames?: ReadonlyArray<string>;
  readonly ProductNames?: ReadonlyArray<string>;
  readonly products?: ReadonlyArray<string>;
  readonly Products?: ReadonlyArray<string>;
  readonly updatedAt?: string;
  readonly UpdatedAt?: string;
  readonly createdAt?: string;
  readonly CreatedAt?: string;
  readonly LastModifiedUtc?: string;
  readonly lastModifiedUtc?: string;
  readonly ModifiedTimestamp?: string;
  readonly modifiedTimestamp?: string;
  readonly archived?: boolean;
  readonly Archived?: boolean;
  readonly isArchived?: boolean;
  readonly IsArchived?: boolean;
  readonly [extra: string]: unknown;
}

export interface ProductDocumentKey {
  readonly pk: string;
  readonly sk: string;
}

/**
 * Dynamo / vector identity for a product or document name.
 *
 * Authored YAML may keep spaces and mixed case (`' RoboDog'`). Lookups trim,
 * collapse internal whitespace, and lowercase so that value matches a stored
 * key of `robodog`. Do not rewrite the YAML on save.
 */
export function normalizeProductNameKey(productName: string | null | undefined): string {
  return (productName ?? "").trim().replace(/\s+/g, " ").toLowerCase();
}

/** Document name keys use the same grammar as product name keys. */
export function normalizeDocumentNameKey(documentName: string | null | undefined): string {
  return normalizeProductNameKey(documentName);
}

export function uniqueNormalizedNameKeys(
  keys: ReadonlyArray<string | null | undefined>,
): ReadonlyArray<string> {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const key of keys) {
    const normalized = normalizeProductNameKey(key);
    if (!normalized || seen.has(normalized)) continue;
    seen.add(normalized);
    out.push(normalized);
  }
  return out;
}

export function sameNormalizedNameKey(
  a: string | null | undefined,
  b: string | null | undefined,
): boolean {
  const left = normalizeProductNameKey(a);
  return left.length > 0 && left === normalizeProductNameKey(b);
}

export function documentByProductPk(accountId: string, productName: string): string {
  return `${accountId}:documentByProduct:${normalizeProductNameKey(productName)}`;
}

export function documentPk(accountId: string): string {
  return `${accountId}:document`;
}

export function documentSk(documentNameKey: string): string {
  const key = normalizeProductNameKey(documentNameKey);
  return key ? `!document:${key}` : "!document:";
}

export function documentNameKeyFromSk(sk: string | null | undefined): string {
  const value = (sk ?? "").trim();
  const extracted = value.startsWith("!document:") ? value.slice("!document:".length) : value;
  return normalizeProductNameKey(extracted);
}

export function productDocumentName(row: ProductDocumentDto): string {
  return firstString(row, ["documentName", "DocumentName", "fileName", "FileName", "name", "Name"])
    ?? docFileNameFromStoragePath(productDocumentStoragePath(row))
    ?? "";
}

/**
 * The Define app's human TITLE for the document — the `name` column on the
 * canonical `{accountId}:document` row (`Robodog Manual`).
 *
 * ⚠ DELIBERATELY NOT `productDocumentName`, and `fileName` is deliberately
 * ABSENT from the key list. That other resolver is IDENTITY, and it is read by
 * five things that all want the uploaded file:
 *   - `productDocumentHasThumbnail` tests its FILE EXTENSION — a title has
 *     none, so thumbnails would silently stop being requested at all
 *   - `productDocumentFormat` tests the same extension for the badge
 *   - the thumbnail S3 key `docs/thumbnails/{fileName}.png`
 *   - `Find_and_Show_PDF`'s exact match
 *   - the vector-store `filename` attach attribute, which the Unity desktop
 *     client writes into the SAME shared store — diverging from it makes the
 *     `restrictToFileName` filter match zero already-attached chunks
 * The row carries BOTH keys, and `productDocumentName` reads `fileName` first,
 * which is why the title was never reached. Do not "fix" that by reordering it.
 *
 * Returns "" when the row states no title, so a caller falls back to the file
 * name and a row without one is unchanged from today.
 */
export function productDocumentDisplayName(row: ProductDocumentDto): string {
  return firstString(row, ["documentName", "DocumentName", "name", "Name"]) ?? "";
}

export function productDocumentNameKey(row: ProductDocumentDto): string {
  const sk = firstString(row, ["sk", "SK"]);
  const fromSk = documentNameKeyFromSk(sk);
  return normalizeProductNameKey(fromSk || productDocumentName(row));
}

export function productDocumentStoragePath(row: ProductDocumentDto): string {
  const direct = firstString(row, ["storagePath", "StoragePath"]);
  if (direct) return direct;
  const fileKey = firstString(row, ["fileKey", "FileKey"]);
  if (fileKey) return storagePathFromFileKey(fileKey);
  const name = firstString(row, ["fileName", "FileName", "documentName", "DocumentName", "name", "Name"]);
  return name ? docStoragePath(name) : "";
}

export function productDocumentContentType(row: ProductDocumentDto): string | undefined {
  return firstString(row, ["contentType", "ContentType", "fileContentType", "FileContentType"]) ?? undefined;
}

export function productDocumentUserTag(row: ProductDocumentDto): string {
  return firstString(row, ["userTag", "UserTag", "uploadedBy", "UploadedBy", "source", "Source"]) ?? "";
}

export function productDocumentFileSize(row: ProductDocumentDto): number {
  return firstNumber(row, [
    "fileSize",
    "FileSize",
    "fileSizeBytes",
    "FileSizeBytes",
    "sizeBytes",
    "SizeBytes",
    "size",
    "Size",
  ]) ?? 0;
}

export function productDocumentLastModifiedMs(row: ProductDocumentDto): number {
  const value = firstString(row, [
    "updatedAt",
    "UpdatedAt",
    "LastModifiedUtc",
    "lastModifiedUtc",
    "ModifiedTimestamp",
    "modifiedTimestamp",
    "createdAt",
    "CreatedAt",
  ]);
  return parseDocLastModified(value ?? undefined);
}

export function productDocumentProducts(row: ProductDocumentDto): ReadonlyArray<string> {
  return firstStringArray(row, [
    "productNames",
    "ProductNames",
    "products",
    "Products",
    "productKeys",
    "ProductKeys",
  ]);
}

export function isProductDocumentArchived(row: ProductDocumentDto): boolean {
  return firstBoolean(row, ["archived", "Archived", "isArchived", "IsArchived"]) ?? false;
}

export function isProductDocumentLinkArchived(row: ProductDocumentLinkDto): boolean {
  return firstBoolean(row, [
    "archived",
    "Archived",
    "isArchived",
    "IsArchived",
    "productArchived",
    "ProductArchived",
    "isProductArchived",
    "IsProductArchived",
  ]) ?? false;
}

export function productDocumentHasThumbnail(row: ProductDocumentDto): boolean {
  if (firstString(row, ["thumbnailUrl", "ThumbnailUrl", "thumbnailStoragePath", "ThumbnailStoragePath"])) {
    return true;
  }
  return fileExtension(productDocumentName(row)) === "pdf";
}

export function productDocumentFormat(row: ProductDocumentDto): string {
  return docFormat(productDocumentName(row) || productDocumentStoragePath(row));
}

function firstString(row: Record<string, unknown>, keys: ReadonlyArray<string>): string | null {
  for (const key of keys) {
    const value = row[key];
    if (typeof value !== "string") continue;
    const trimmed = value.trim();
    if (trimmed) return trimmed;
  }
  return null;
}

function storagePathFromFileKey(fileKey: string): string {
  const trimmed = fileKey.trim().replace(/\\/g, "/").replace(/^\/+/, "");
  const marker = "/projectdata/";
  const markerIndex = trimmed.toLowerCase().indexOf(marker);
  if (markerIndex >= 0) return trimmed.slice(markerIndex + marker.length);
  const projectDataPrefix = "projectdata/";
  if (trimmed.toLowerCase().startsWith(projectDataPrefix)) {
    return trimmed.slice(projectDataPrefix.length);
  }
  return trimmed;
}

function firstNumber(row: Record<string, unknown>, keys: ReadonlyArray<string>): number | null {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === "number" && Number.isFinite(value)) return Math.max(0, value);
    if (typeof value === "string") {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) return Math.max(0, parsed);
    }
  }
  return null;
}

function firstBoolean(row: Record<string, unknown>, keys: ReadonlyArray<string>): boolean | null {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value !== 0;
    if (typeof value === "string") {
      const lower = value.trim().toLowerCase();
      if (lower === "true" || lower === "1" || lower === "yes") return true;
      if (lower === "false" || lower === "0" || lower === "no") return false;
    }
  }
  return null;
}

function firstStringArray(row: Record<string, unknown>, keys: ReadonlyArray<string>): ReadonlyArray<string> {
  for (const key of keys) {
    const value = row[key];
    if (!Array.isArray(value)) continue;
    const strings = value
      .filter((item): item is string => typeof item === "string")
      .map((item) => item.trim())
      .filter(Boolean);
    if (strings.length > 0) return strings;
  }
  return [];
}
